<template>
    <Card class="p-4 mb-4" :header="$t('pluginAppearance.title')">
        <b-overlay :show="submitting" rounded="sm" class="w-100">
            <b-form @submit.prevent="handleSubmit">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <b-form-group :label="$t('pluginAppearance.version')">
                            <b-form-select v-model="model.version" :options="versionOptions" :disabled="submitting" />
                        </b-form-group>
                    </div>

                    <div class="col-md-6 mb-3">
                        <b-form-group :label="$t('pluginAppearance.language')">
                            <b-form-select v-model="model.language" :options="languageOptions" :disabled="submitting" />
                        </b-form-group>
                    </div>

                    <div class="col-md-6 mb-3">
                        <FormValidator :validator="$v.model.leadColor" :label="$t('pluginAppearance.leadColor')">
                            <FormColorPicker v-model="model.leadColor" :disabled="submitting" />
                        </FormValidator>
                    </div>

                    <div class="col-md-6 mb-3">
                        <FormValidator :validator="$v.model.buttonLabel" :label="$t('pluginAppearance.buttonLabel')">
                            <b-form-input v-model="model.buttonLabel" :disabled="submitting" :maxlength="buttonLabelMaxLength" />
                        </FormValidator>
                    </div>

                    <div class="col-md-6 mb-3">
                        <b-form-group :label="$t('pluginAppearance.shopStyle')">
                            <b-form-select v-model="model.shopStyle" :options="shopStyleOptions" :disabled="submitting" />
                        </b-form-group>
                    </div>

                    <div class="col-md-6 mb-3">
                        <b-form-group :label="$t('pluginAppearance.availability')">
                            <Multiselect
                                v-model="model.availabilities"
                                :options="availabilities"
                                :placeholder="$t('pluginAppearance.availability')"
                                track-by="value"
                                label="label"
                                select-label=""
                                show-checkboxes
                                show-selected
                                multiple
                                :disabled="submitting"
                                :close-on-select="false"
                                :clear-on-select="false"
                                preselect-all
                            />
                        </b-form-group>
                    </div>

                    <div class="col-md-12 mb-3">
                        <b-form-checkbox v-model="model.showProduct" :disabled="submitting">{{ $t('pluginAppearance.showProduct') }}</b-form-checkbox>
                    </div>

                    <div class="col-md-12 mb-3">
                        <b-form-checkbox v-model="model.showPrices" :disabled="submitting">{{ $t('pluginAppearance.showPrices') }}</b-form-checkbox>
                    </div>

                    <div class="col-md-12 mb-3">
                        <b-form-checkbox v-model="model.showAvailability" :disabled="submitting">{{ $t('pluginAppearance.showAvailability') }}</b-form-checkbox>
                    </div>
                </div>

                <p class="mt-4 font-weight-bold">{{ $t('pluginAppearance.additionalColumnSection') }}</p>

                <b-form-checkbox v-model="model.showAdditionalColumn" :disabled="submitting">{{ $t('pluginAppearance.showAdditionalColumn') }}</b-form-checkbox>

                <b-form-textarea
                    v-if="model.showAdditionalColumn"
                    v-model="model.additionalColumn"
                    :placeholder="$t('pluginAppearance.additionalColumn')"
                    :disabled="submitting"
                    :maxlength="additionalColumnMaxLength"
                    class="mt-3"
                />

                <b-form-checkbox v-if="model.showAdditionalColumn" v-model="model.additionalColumnOnlyForSorted" class="mt-4" :disabled="submitting">
                    {{ $t('pluginAppearance.additionalColumnOnlyForSorted') }}
                </b-form-checkbox>

                <p class="mt-4 font-weight-bold">{{ $t('pluginAppearance.sectionSorting') }}</p>

                <b-form-radio-group v-model="model.sortType" :disabled="submitting">
                    <b-form-radio v-for="sortType in sortTypes" :key="sortType.id" :value="sortType.value">
                        {{ sortType.label }}
                    </b-form-radio>
                </b-form-radio-group>

                <p class="mt-4 font-weight-bold">{{ $t('pluginAppearance.sectionShoppingList') }}</p>
                <p>{{ $t('pluginAppearance.shoppingListDescription') }}</p>

                <FormValidator :validator="$v.model.rowCount" :label="$t('pluginAppearance.displayedShops')" v-slot="slotProps" :msg-props="{ min: $v.model.rowCount.$params.minValue.min, max: $v.model.rowCount.$params.maxValue.max }">
                    <b-form-input
                        v-model="model.rowCount"
                        type="number"
                        :disabled="submitting"
                        :state="slotProps.state"
                    />
                </FormValidator>

                <ErrorMessage v-if="error" :value="error" class="mt-3" />

                <ButtonIcon class="mt-4" type="submit" hide-label-while-loading :loading="submitting" :disabled="submitting">
                    {{ $t('pluginAppearance.submit') }}
                </ButtonIcon>
            </b-form>
        </b-overlay>
    </Card>
</template>

<script>
    import { maxLength, required, minValue, maxValue } from 'vuelidate/lib/validators';
    import FormValidator from '@/components/common/FormValidator.vue';
    import FormColorPicker from '@/components/common/FormColorPicker.vue';
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import pluginService, { widgetVersions, translateWidgetVersion } from '@/services/pluginService';
    import { getLanguageOptions } from '@/i18n/i18n';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import Multiselect from '@/components/common/Multiselect.vue';

    const BUTTON_LABEL_MAX_LENGTH = 15;
    const ADDITIONAL_COLUMN_MAX_LENGTH = 65535;

    export default {
        name: 'PluginAppearance',
        components: {
            FormValidator,
            FormColorPicker,
            ButtonIcon,
            ErrorMessage,
            Multiselect,
        },
        props: {
            plugin: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                model: {
                    sortType: null,
                    rowCount: 5,
                    leadColor: '',
                    buttonLabel: '',
                    showProduct: false,
                    showPrices: false,
                    showAvailability: false,
                    shopStyle: null,
                    showAdditionalColumn: false,
                    additionalColumn: '',
                    additionalColumnOnlyForSorted: false,
                    version: 'latest',
                    language: null,
                    availabilities: [],
                },
                languageOptions: getLanguageOptions(true),
                shopStyleOptions: [
                    { value: 'icon_name', text: this.$t('pluginAppearance.shopStyleIconName') },
                    { value: 'logo', text: this.$t('pluginAppearance.shopStyleLogo') },
                ],
                buttonLabelMaxLength: BUTTON_LABEL_MAX_LENGTH,
                additionalColumnMaxLength: ADDITIONAL_COLUMN_MAX_LENGTH,
                error: null,
                submitting: false,

                sortTypes: [],
                availabilities: [],
            };
        },
        validations: {
            model: {
                version: { required },
                language: { required },
                buttonLabel: { maxLength: maxLength(BUTTON_LABEL_MAX_LENGTH) },
                additionalColumn: { maxLength: maxLength(ADDITIONAL_COLUMN_MAX_LENGTH) },
                rowCount: {
                    minValue: minValue(0),
                    maxValue: maxValue(50),
                },
            },
        },
        computed: {
            versionOptions() {
                return widgetVersions.map(v => ({ value: v, text: translateWidgetVersion(v) }));
            },
        },
        watch: {
            'model.showAdditionalColumn': {
                handler(val) {
                    if (!val) {
                        this.model.additionalColumn = '';
                        this.model.additionalColumnOnlyForSorted = false;
                    }
                },
            },
        },
        methods: {
            async handleSubmit() {
                this.error = null;
                this.$v.$touch();
                if (this.$v.$invalid) return;
                this.submitting = true;
                try {
                    delete this.model.showAdditionalColumn;

                    await pluginService.updatePlugin(this.plugin.id, {
                        ...this.model,
                        availabilities: this.model.availabilities.map(a => a.value ?? a),
                        version: this.model.version === 'latest' ? null : this.model.version,
                    });
                    this.$toastr.success(this.$t('pluginAppearance.saveSuccess'));
                } catch (e) {
                    this.error = e;
                }
                this.submitting = false;
            },

            async fetchSortTypes() {
                try {
                    this.sortTypes = await pluginService.fetchSortTypes();
                } catch (e) {
                    this.error = e;
                }
            },

            async fetchAvailabilities() {
                try {
                    this.availabilities = (await pluginService.fetchOfferAvailabilities()).items;
                } catch (e) {
                    this.error = e;
                }
            },
        },
        created() {
            if (this.plugin) {
                Object.assign(this.model, {
                    rowCount: this.plugin.rowCount ?? 5,
                    leadColor: this.plugin.leadColor ?? '',
                    buttonLabel: this.plugin.buttonLabel ?? '',
                    showProduct: this.plugin.showProduct ?? false,
                    showPrices: this.plugin.showPrices ?? false,
                    showAvailability: this.plugin.showAvailability ?? false,
                    shopStyle: this.plugin.shopStyle ?? 'icon_name',
                    showAdditionalColumn: Boolean(this.plugin.additionalColumn),
                    additionalColumn: this.plugin.additionalColumn ?? '',
                    additionalColumnOnlyForSorted: this.plugin.additionalColumnOnlyForSorted ?? false,
                    version: this.plugin.version ?? 'latest',
                    language: this.plugin.language ?? null,
                    availabilities: this.plugin.availabilities ?? [],
                });
            }
            this.fetchSortTypes();
            this.fetchAvailabilities();
        },
    };
</script>
